import { PriceFormattingOptions } from './types';
import { roundNumberByStrategy } from './roundNumberByStrategy';

/**
 * Round a number (up) to the first n digits
 * @example 12.34 => 12.34
 * @example 12345.67 => 12350
 * @example 1234567.89 => 1235000
 */
const relativeRound = (
  value: number,
  numberOfDigitsToKeep: number = 3
): number => {
  // Log will error out on 0 so just return 0 back
  if (!value) return 0;
  // Determine the level of rounding that is needed
  const tier = Math.floor(Math.log10(Math.abs(value)));
  // Keep the first n digits of the number
  const roundingLevel = tier - numberOfDigitsToKeep;
  // Divide the number by the rounding level to set up the round
  const adjusted = value / Math.pow(10, roundingLevel);
  // Always round up to be on the safe side and
  const rounded = Math[value < 0 ? 'floor' : 'ceil'](adjusted);
  // multiply by the rounding level to get the number back in the correct order
  // of magnitude
  return parseFloat((rounded * Math.pow(10, roundingLevel)).toPrecision(12));
};

/**
 * Formats a price in pennies to be a price in dollars.
 */
export const formatPrice = (
  price: number,
  {
    roundingStrategy,
    maxDecimals = 2,
    minDecimals = 0,
    useRelativeRounding = false,
    isPriceGivenAsCents = true,
  }: Partial<PriceFormattingOptions> = {} as PriceFormattingOptions
): string => {
  // round if we need to
  const rounded = useRelativeRounding ? relativeRound(price) : price;

  // Price is given in pennies so convert to dollars
  let priceInDollars = isPriceGivenAsCents ? rounded / 100 : rounded;

  if (roundingStrategy && maxDecimals <= 2) {
    priceInDollars = roundNumberByStrategy(priceInDollars, roundingStrategy);
  }

  // Only show decimals if we have pennies or we are forcing them
  const hasPennies = priceInDollars % 1 !== 0;

  const options = {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: minDecimals === 0 && hasPennies ? 2 : minDecimals,
  };

  // format the price in the user's locale
  return priceInDollars.toLocaleString(undefined, options);
};
